import { faHeart as faHeartOutline } from "@fortawesome/free-regular-svg-icons";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import NameTile from "../components/NameTile";
import { Breadcrumb, SearchInput, SortButtons, FilterCount, Loading } from "../components/ui";

const ITEMS_PER_PAGE = 30;

const LikedNamesPage = () => {
  const [loading, setLoading] = useState(true);
  const [likedNames, setLikedNames] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("project_name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const host = process.env.REACT_APP_SERVER;

  useEffect(() => {
    const fetchLikedNames = async () => {
      try {
        const response = await fetch(`${host}/api/liked-names`);
        if (response.ok) {
          const data = await response.json();
          // Map the data to ensure NameTile compatibility
          const names = (data.data.likedNames || []).map(n => ({
            ...n,
            bookmarked: true,
            // Use overall_grade as trademark if trademark not set
            trademark: n.trademark || n.overall_grade,
          }));
          setLikedNames(names);
        }
      } catch (error) {
        console.error("Failed to fetch liked names:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLikedNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter by search
  const filteredNames = likedNames.filter(name => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      name.name?.toLowerCase().includes(query) ||
      name.project_name?.toLowerCase().includes(query)
    );
  });

  // Sort names
  const sortedNames = [...filteredNames].sort((a, b) => {
    const direction = sortDirection === "asc" ? 1 : -1;
    let valueA = a[sortColumn];
    let valueB = b[sortColumn];

    // Handle grade sorting
    if (sortColumn === "trademark") {
      const gradeOrder = ["A", "B", "C", "D", "F"];
      const indexA = gradeOrder.indexOf(valueA);
      const indexB = gradeOrder.indexOf(valueB);
      if (indexA !== -1 && indexB !== -1) {
        return direction * (indexA - indexB);
      }
    }

    if (typeof valueA === "string" && typeof valueB === "string") {
      return direction * (valueA || "").localeCompare(valueB || "");
    }
    return direction * ((valueA || 0) - (valueB || 0));
  });

  // Pagination
  const totalPages = Math.ceil(sortedNames.length / ITEMS_PER_PAGE);
  const paginatedNames = sortedNames.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Group names by sort column for dividers
  const getGroupKey = (name, column) => {
    if (column === "project_name") return name.project_name || "Unknown Project";
    if (column === "trademark") return name.trademark || "No Grade";
    if (column === "name") return (name.name?.[0] || "?").toUpperCase();
    return "";
  };

  const groupNames = (names, column) => {
    const groups = [];
    let currentGroup = null;
    let currentNames = [];

    names.forEach(name => {
      const groupKey = getGroupKey(name, column);
      if (currentGroup !== groupKey) {
        if (currentNames.length > 0) {
          groups.push({ key: currentGroup, names: currentNames });
        }
        currentGroup = groupKey;
        currentNames = [name];
      } else {
        currentNames.push(name);
      }
    });
    if (currentNames.length > 0) {
      groups.push({ key: currentGroup, names: currentNames });
    }
    return groups;
  };

  // Get unique project count
  const projectCount = new Set(likedNames.map(name => name.project_id)).size;

  // Handle bookmark toggle (unliking removes from list)
  const handleBookmark = async (name) => {
    try {
      const response = await fetch(`${host}/api/projects/${name.project_id}/names/${name.id}/bookmark`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookmarked: false }),
      });

      if (response.ok) {
        // Remove from list since it's no longer liked
        setLikedNames(prev => prev.filter(n => n.id !== name.id));
      }
    } catch (error) {
      console.error("Failed to update bookmark:", error);
    }
  };

  if (loading) {
    return <Loading size="lg" message="Loading liked names..." fullScreen />;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Liked Names" },
        ]}
        className="mb-4"
      />

      {/* Header Card */}
      <div className="bg-base-100 rounded-lg p-4 sm:p-5 mb-6 border border-base-content/10">
        {/* Title */}
        <div className="flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faHeartOutline} className="text-base-content/40" />
          <h1 className="text-2xl font-semibold text-base-content">Liked Names</h1>
        </div>

        {/* Search */}
        <SearchInput
          value={searchQuery}
          onChange={value => { setSearchQuery(value); setCurrentPage(1); }}
          placeholder="Search names or projects..."
          className="mb-4"
        />

        {/* Sort + Count */}
        <div className="flex items-center justify-between">
          <SortButtons
            options={[
              { key: "project_name", label: "Project" },
              { key: "name", label: "Name" },
              { key: "trademark", label: "Grade" },
            ]}
            value={sortColumn}
            direction={sortDirection}
            onChange={(key, dir) => { setSortColumn(key); setSortDirection(dir); setCurrentPage(1); }}
          />
          <span className="text-xs text-base-content/40">
            <FilterCount filtered={filteredNames.length} total={likedNames.length} label="names" />
            {" "}from {projectCount} projects
          </span>
        </div>
      </div>

      {/* Names Grid with Dividers */}
      {paginatedNames.length > 0 ? (
        groupNames(paginatedNames, sortColumn).map((group, idx) => (
          <div key={group.key} className={idx > 0 ? "mt-6" : ""}>
            <div className="mb-3">
              <span className="text-sm font-medium text-base-content/60">{group.key}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {group.names.map(name => (
                <NameTile
                  key={name.id}
                  name={name}
                  projectId={name.project_id}
                  size="small"
                  onBookmark={handleBookmark}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-12 text-base-content/50">
          {searchQuery ? `No names found matching "${searchQuery}"` : "No liked names yet"}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded disabled:opacity-30"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="text-sm text-base-content/60">
            {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded disabled:opacity-30"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
};

export default LikedNamesPage;
